import React, { useState, useEffect, useRef } from "react";
import {
  Server,
  ArrowRight,
  Code2,
  Zap,
  Database,
  ChevronDown,
  Copy,
  Check,
  Radio,
  Clock,
  Layers,
  Download,
} from "lucide-react";
import axios from "axios";

/**
 * SirsApiFlow — educational panel showing HOW the Analisis page fetches data
 * from the SIRS API (now a PHP script at SIRS_API_BASE_URL, configurable).
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    icon: Layers,
    title: "1. Browser (React)",
    desc: "Halaman Analisis memanggil axios.get('/analisis/kunjungan') dengan Authorization: Bearer <JWT>.",
    tint: "bg-blue-50 text-blue-700 border-blue-100",
  },
  {
    icon: Server,
    title: "2. Backend FastAPI",
    desc: "Endpoint /api/analisis/{sub} memverifikasi JWT admin, lalu HTTP GET ke SIRS_API_BASE_URL/sirs/... via httpx.",
    tint: "bg-indigo-50 text-indigo-700 border-indigo-100",
  },
  {
    icon: Zap,
    title: "3. PHP SIRS Server",
    desc: "Script PHP (sirs_api.php) menghasilkan data + jitter, menyimulasikan latency ~120ms. Portable ke server SIRS asli.",
    tint: "bg-amber-50 text-amber-700 border-amber-100",
  },
  {
    icon: Database,
    title: "4. Merge + Render",
    desc: "Backend menggabungkan override admin (PDF, catatan) dari MongoDB, kembalikan JSON ke browser. Recharts merender chart.",
    tint: "bg-emerald-50 text-emerald-700 border-emerald-100",
  },
];

const SirsApiFlow = ({ sub = "kunjungan", year, month }) => {
  const [open, setOpen] = useState(false);
  const [rawResp, setRawResp] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [elapsedMs, setElapsedMs] = useState(0);
  const [copied, setCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [sirsInfo, setSirsInfo] = useState(null);
  const abortRef = useRef(null);

  const endpoint = `${API}/sirs/${sub}?year=${year}&month=${month}`;
  const relEndpoint = `/api/sirs/${sub}?year=${year}&month=${month}`;
  const downloadPhpUrl = `${API}/sirs/download-php-source`;
  const downloadPhpMariadbUrl = `${API}/sirs/download-php-mariadb`;

  // Step 1 description reflects the current data source (sub).
  const steps = STEPS.map((s, i) =>
    i === 0
      ? { ...s, desc: `Halaman SIPERKASA memanggil axios.get('/analisis/${sub}') dengan Authorization: Bearer <JWT>.` }
      : s
  );

  // Fetch current SIRS configuration once
  useEffect(() => {
    axios
      .get(`${API}/sirs/info`)
      .then((r) => setSirsInfo(r.data))
      .catch(() => {});
  }, []);

  const runCall = async () => {
    setStatus("loading");
    setRawResp(null);
    setErrorMsg("");
    setElapsedMs(0);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const started = performance.now();
    try {
      const r = await axios.get(endpoint, {
        signal: controller.signal,
        timeout: 8000,
      });
      const took = Math.max(1, Math.round(performance.now() - started));
      setElapsedMs(took);
      setRawResp(r.data);
      setStatus("success");
    } catch (e) {
      if (axios.isCancel(e) || e.name === "CanceledError") return;
      setErrorMsg(e?.response?.data?.detail || e?.message || "Failed");
      setStatus("error");
      setElapsedMs(Math.round(performance.now() - started));
    }
  };

  // Auto-fire once when opened
  useEffect(() => {
    if (open && status === "idle") runCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Re-fire when period changes while panel is open
  useEffect(() => {
    if (open) runCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, year, month]);

  const copyEndpoint = async () => {
    try {
      await navigator.clipboard.writeText(relEndpoint);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const jsonPreview = rawResp
    ? JSON.stringify(rawResp, null, 2)
    : "";

  return (
    <div
      data-testid="sirs-api-flow"
      className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="sirs-api-flow-toggle"
        className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-3 text-left">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center">
            <Code2 className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-slate-900 flex items-center gap-2">
              Cara Kerja: Data dari Mock SIRS API
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-[10px] font-semibold text-emerald-700">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                LIVE
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Klik untuk melihat alur pengambilan data & respons JSON aktual dari SIRS.
            </div>
          </div>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-slate-100 p-5 md:p-6 space-y-6 bg-slate-50/40">
          {/* Flow diagram */}
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3">
              Alur Data
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="sirs-flow-steps">
              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="relative">
                    <div className={`h-full rounded-2xl border ${s.tint} p-4`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-8 w-8 rounded-lg bg-white/70 flex items-center justify-center">
                          <Icon className="h-4 w-4" />
                        </div>
                      </div>
                      <div className="text-sm font-semibold mb-1">{s.title}</div>
                      <div className="text-xs leading-relaxed opacity-90">{s.desc}</div>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="hidden md:flex absolute top-1/2 -right-2 -translate-y-1/2 z-10">
                        <ArrowRight className="h-4 w-4 text-slate-400 bg-slate-50 rounded-full" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* SIRS deployment config */}
          {sirsInfo && (
            <div
              data-testid="sirs-config-strip"
              className="rounded-2xl border border-slate-200 bg-white p-4"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">
                    SIRS Base URL yang aktif
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <code
                      data-testid="sirs-config-base-url"
                      className="font-mono text-sm text-slate-800 bg-slate-100 px-2 py-1 rounded"
                    >
                      {sirsInfo.base_url}
                    </code>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        sirsInfo.type === "external_sirs"
                          ? "bg-purple-50 text-purple-700 border border-purple-100"
                          : "bg-amber-50 text-amber-700 border border-amber-100"
                      }`}
                    >
                      {sirsInfo.type === "external_sirs"
                        ? "SIRS EKSTERNAL"
                        : "MOCK PHP LOKAL"}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                    Ubah dengan env var{" "}
                    <code className="font-mono text-slate-700">SIRS_API_BASE_URL</code>{" "}
                    di <code className="font-mono text-slate-700">backend/.env</code>, lalu restart backend.
                  </div>
                </div>
                <a
                  href={downloadPhpUrl}
                  data-testid="sirs-download-php"
                  className="shrink-0 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl"
                >
                  <Download className="h-4 w-4" />
                  Unduh script PHP
                </a>
                <a
                  href={downloadPhpMariadbUrl}
                  data-testid="sirs-download-php-mariadb"
                  className="shrink-0 inline-flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl"
                  title="Template PHP untuk mengambil data dari database MariaDB SIMRS"
                >
                  <Database className="h-4 w-4" />
                  Template MariaDB SIMRS
                </a>
              </div>
              <details className="mt-3 group">
                <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-800 select-none">
                  ▸ Cara deploy ke server SIRS Anda
                </summary>
                <ol className="mt-2 text-xs text-slate-600 leading-relaxed list-decimal ml-5 space-y-1">
                  <li>Untuk demo cepat: unduh <b>Unduh script PHP</b> (<code className="font-mono">sirs_api.php</code>, data simulasi).</li>
                  <li>Untuk data ASLI: unduh <b>Template MariaDB SIMRS</b> (<code className="font-mono">sirs_simrs_mariadb.php</code>), lalu isi kredensial DB & sesuaikan query SQL (bagian <code className="font-mono">TODO</code>) sesuai skema SIMRS Anda.</li>
                  <li>Unggah ke server SIRS, contoh: <code className="font-mono">/var/www/html/sirs/sirs_simrs_mariadb.php</code>.</li>
                  <li>Uji: <code className="font-mono">https://sirs.rs-anda.co.id/sirs_simrs_mariadb.php/sirs/health</code> — harus balas JSON (<code className="font-mono">db_connected: true</code>).</li>
                  <li>Set env: <code className="font-mono">SIRS_API_BASE_URL=https://sirs.rs-anda.co.id/sirs_simrs_mariadb.php</code></li>
                  <li>Restart backend: <code className="font-mono">sudo supervisorctl restart backend</code>.</li>
                </ol>
              </details>
            </div>
          )}

          {/* Live call panel */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center gap-2 px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2 text-sm flex-1 min-w-0">
                <span className="px-2 py-1 rounded-lg bg-blue-800 text-white text-[10px] font-bold tracking-wider">
                  GET
                </span>
                <code
                  data-testid="sirs-api-flow-endpoint"
                  className="font-mono text-xs text-slate-700 truncate"
                  title={relEndpoint}
                >
                  {relEndpoint}
                </code>
                <button
                  type="button"
                  onClick={copyEndpoint}
                  data-testid="sirs-api-flow-copy"
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-slate-500 hover:bg-slate-100"
                >
                  {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                  {copied ? "Disalin" : "Salin"}
                </button>
              </div>
              <div className="flex items-center gap-2">
                {status === "loading" && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-100 px-2 py-1 rounded-full">
                    <Radio className="h-3 w-3 animate-pulse" />
                    Memanggil…
                  </span>
                )}
                {status === "success" && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-full">
                    <Check className="h-3 w-3" />
                    200 OK
                  </span>
                )}
                {status === "error" && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-100 px-2 py-1 rounded-full">
                    Error
                  </span>
                )}
                {elapsedMs > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500 border border-slate-200 px-2 py-1 rounded-full">
                    <Clock className="h-3 w-3" />
                    {elapsedMs} ms
                  </span>
                )}
                <button
                  type="button"
                  onClick={runCall}
                  data-testid="sirs-api-flow-refetch"
                  disabled={status === "loading"}
                  className="text-xs font-medium bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white px-3 py-1.5 rounded-full"
                >
                  Panggil Ulang
                </button>
              </div>
            </div>
            <div className="p-4">
              {status === "error" && (
                <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {errorMsg || "Gagal memanggil endpoint."}
                </div>
              )}
              {rawResp?._source && (
                <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800 font-semibold">
                    _source: {rawResp._source}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    _version: {rawResp._version}
                  </span>
                  {rawResp.period_label && (
                    <span className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-blue-800">
                      period: {rawResp.period_label}
                    </span>
                  )}
                  {rawResp._fetched_at && (
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                      _fetched_at: {new Date(rawResp._fetched_at).toLocaleTimeString("id-ID")}
                    </span>
                  )}
                </div>
              )}
              <pre
                data-testid="sirs-api-flow-json"
                className="max-h-72 overflow-auto rounded-xl bg-slate-950 text-slate-100 p-4 text-[11px] font-mono leading-relaxed"
              >
                {status === "loading" && !jsonPreview ? "// memanggil endpoint…" : jsonPreview || "// belum ada data"}
              </pre>
              <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                <p>
                  ⚙️ Endpoint di atas adalah <b>mock</b> yang dijalankan di dalam backend
                  (<code className="font-mono text-slate-700">backend/mock_sirs_api.py</code>).
                  Data dihasilkan secara deterministik per periode + jitter kecil,
                  sehingga chart bisa dites ulang tanpa server SIRS asli.
                </p>
                <p className="mt-2">
                  Dalam integrasi produksi, backend akan meneruskan panggilan ini
                  ke SIRS asli melalui HTTP dengan autentikasi rumah sakit.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SirsApiFlow;
